/**
 * CustomNode - Main node component for text, image, and URL nodes
 *
 * Features:
 * - Semantic zoom adaptation
 * - Cluster color visualization
 * - Content preview with different levels of detail
 */

import React, { memo, useMemo, useState, useRef, useEffect } from 'react';
import { Handle, Position } from '@xyflow/react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText,
  Image,
  Link,
  ExternalLink,
  MoreHorizontal,
  Tag,
  Paperclip,
  Plus,
  Pencil,
  Trash2,
} from 'lucide-react';
import { format } from 'date-fns';
import { useMapStore } from '../../stores/mapStore';

const CustomNode = memo(({ data, selected, id }) => {
  const {
    content,
    type,
    title,
    cluster_tag,
    clusterColor,
    image_url,
    source_url,
    additional_context,
    attachments = [],
    zoomLevel = 'close',
    created_at,
  } = data;

  const { openAttachmentModal, deleteNode, openEditModal } = useMapStore();
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu]);

  // Get icon based on type
  const Icon = useMemo(() => {
    switch (type) {
      case 'image': return Image;
      case 'url': return Link;
      default: return FileText;
    }
  }, [type]);

  // Truncate content based on zoom level
  const displayContent = useMemo(() => {
    if (zoomLevel === 'far') return null;
    if (zoomLevel === 'mid') return content?.substring(0, 80);
    return content?.substring(0, 200);
  }, [content, zoomLevel]);

  // Node size based on zoom level
  const nodeSize = useMemo(() => {
    if (zoomLevel === 'far') return 'w-8 h-8';
    if (zoomLevel === 'mid') return 'w-48';
    return 'w-64';
  }, [zoomLevel]);

  // Far zoom - just show a dot/icon
  if (zoomLevel === 'far') {
    return (
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        className={`${nodeSize} rounded-full flex items-center justify-center transition-all duration-200 ${
          selected ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-950' : ''
        }`}
        style={{
          backgroundColor: clusterColor?.border || '#6366f1',
        }}
      >
        <Handle type="target" position={Position.Top} className="!bg-transparent !border-0" />
        <Handle type="source" position={Position.Bottom} className="!bg-transparent !border-0" />
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className={`${nodeSize} group relative`}
    >
      {/* Connection handles */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !bg-gray-600 !border-2 !border-gray-800 opacity-0 group-hover:opacity-100 transition-opacity"
      />
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-3 !h-3 !bg-gray-600 !border-2 !border-gray-800 opacity-0 group-hover:opacity-100 transition-opacity"
      />
      <Handle
        type="source"
        position={Position.Left}
        id="left"
        className="!w-3 !h-3 !bg-gray-600 !border-2 !border-gray-800 opacity-0 group-hover:opacity-100 transition-opacity"
      />
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        className="!w-3 !h-3 !bg-gray-600 !border-2 !border-gray-800 opacity-0 group-hover:opacity-100 transition-opacity"
      />

      {/* Main node card */}
      <div
        className={`relative rounded-xl overflow-hidden transition-all duration-200 ${
          selected
            ? 'ring-2 ring-indigo-400 shadow-lg shadow-indigo-500/20'
            : 'hover:shadow-lg hover:shadow-gray-900/50'
        }`}
        style={{
          backgroundColor: clusterColor?.bg || 'rgba(99, 102, 241, 0.1)',
          borderWidth: 1,
          borderColor: clusterColor?.border || '#6366f1',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center gap-2 px-3 py-2 border-b"
          style={{ borderColor: `${clusterColor?.border}33` || 'rgba(99, 102, 241, 0.2)' }}
        >
          <Icon
            size={14}
            style={{ color: clusterColor?.text || '#818cf8' }}
          />
          <span
            className="flex-1 text-sm font-medium truncate"
            style={{ color: clusterColor?.text || '#e0e7ff' }}
          >
            {title || 'Untitled'}
          </span>
          <div className="relative" ref={menuRef}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu(!showMenu);
              }}
              onMouseDown={(e) => e.stopPropagation()}
              className="nodrag nopan p-1 rounded hover:bg-gray-700/50 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <MoreHorizontal size={12} className="text-gray-400" />
            </button>

            {/* Dropdown Menu */}
            <AnimatePresence>
              {showMenu && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: -5 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -5 }}
                  transition={{ duration: 0.1 }}
                  className="nodrag nopan absolute right-0 top-full mt-1 z-50 min-w-[140px] bg-gray-800 border border-gray-700 rounded-lg shadow-xl overflow-hidden"
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMenu(false);
                      openEditModal?.(id);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-gray-700 transition-colors"
                  >
                    <Pencil size={14} />
                    <span>Düzenle</span>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMenu(false);
                      openAttachmentModal?.(id);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-gray-700 transition-colors"
                  >
                    <Paperclip size={14} />
                    <span>Dosya Ekle</span>
                  </button>
                  <div className="border-t border-gray-700" />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMenu(false);
                      if (confirm('Bu notu silmek istediğinizden emin misiniz?')) {
                        deleteNode?.(id);
                      }
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                  >
                    <Trash2 size={14} />
                    <span>Sil</span>
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Content area */}
        <div className="p-3">
          {/* Image preview */}
          {type === 'image' && image_url && (
            <div className="mb-2 rounded-lg overflow-hidden bg-gray-900/50">
              <img
                src={image_url}
                alt={title}
                className="w-full h-24 object-cover"
              />
            </div>
          )}

          {/* Text content */}
          {displayContent && (
            <p className="text-sm text-gray-300 leading-relaxed">
              {displayContent}
              {content?.length > displayContent?.length && (
                <span className="text-gray-500">...</span>
              )}
            </p>
          )}

          {/* URL preview */}
          {type === 'url' && source_url && zoomLevel === 'close' && (
            <a
              href={source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 mt-2 text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
            >
              <ExternalLink size={10} />
              <span className="truncate">{new URL(source_url).hostname}</span>
            </a>
          )}
        </div>

        {/* Footer with metadata */}
        {zoomLevel === 'close' && (cluster_tag || created_at || attachments?.length > 0) && (
          <div
            className="flex items-center justify-between px-3 py-1.5 text-xs border-t"
            style={{ borderColor: `${clusterColor?.border}33` || 'rgba(99, 102, 241, 0.2)' }}
          >
            <div className="flex items-center gap-2">
              {cluster_tag && (
                <div className="flex items-center gap-1">
                  <Tag size={10} className="text-gray-500" />
                  <span className="text-gray-500">{cluster_tag}</span>
                </div>
              )}
              {/* Attachment indicator */}
              {attachments?.length > 0 && (
                <div className="flex items-center gap-1 text-indigo-400">
                  <Paperclip size={10} />
                  <span>{attachments.length}</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* Add attachment button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openAttachmentModal?.(id);
                }}
                className="p-1 rounded hover:bg-gray-700/50 opacity-0 group-hover:opacity-100 transition-opacity"
                title="Dosya ekle"
              >
                <Plus size={10} className="text-gray-400" />
              </button>
              {created_at && (
                <span className="text-gray-600">
                  {format(new Date(created_at), 'MMM d')}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Additional context indicator */}
        {additional_context && zoomLevel === 'close' && (
          <div className="px-3 py-1 bg-indigo-500/10 border-t border-indigo-500/20">
            <div className="flex items-center gap-1 text-[10px] text-indigo-400">
              <FileText size={10} />
              <span>Ek bağlam mevcut</span>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
});

CustomNode.displayName = 'CustomNode';

export default CustomNode;
